import type { TodayAllocation, TradeOrder } from "../stores/backtestStore";

interface TodayActivityProps {
  allocation: TodayAllocation;
}

// Check if allocation date is stale - allocation should ALWAYS be for today.
// If it's not today, the backtest didn't have today's prices.
function isAllocationStale(allocationDate: string): { isStale: boolean; todayStr: string } {
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
  
  return {
    isStale: allocationDate !== todayStr,
    todayStr,
  };
}

export function TodayActivity({ allocation }: TodayActivityProps) {
  const { positions, portfolioValue, trades, date } = allocation;
  const staleCheck = isAllocationStale(date);

  const sortedPositions = Object.entries(positions)
    .sort(([, a], [, b]) => b - a)
    .filter(([, weight]) => weight > 0.001);

  if (sortedPositions.length === 0) {
    return (
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-surface-100 mb-4">
          Today's Activity
        </h3>
        <p className="text-surface-400">No allocation data available</p>
      </div>
    );
  }

  const sellTrades = trades.filter((t) => t.action === "SELL");
  const buyTrades = trades.filter((t) => t.action === "BUY");

  return (
    <div className="glass-card p-6">
      {/* Stale Data Warning */}
      {staleCheck.isStale && (
        <div className="stale-warning mb-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
          <div className="flex items-start gap-2">
            <svg className="stale-warning-icon w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <span className="stale-warning-message text-sm text-yellow-300/80">
                Allocation data is from <strong>{date}</strong> (expected {staleCheck.todayStr}). 
                This may indicate stale cached prices.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-surface-100">
          Today's Activity
        </h3>
        <span className={`text-sm ${staleCheck.isStale ? 'text-yellow-400' : 'text-surface-400'}`}>{date}</span>
      </div>

      {/* Portfolio Value */}
      <div className="mb-6 p-4 bg-surface-800/50 rounded-lg">
        <div className="text-surface-400 text-sm mb-1">Portfolio Value</div>
        <div className="text-2xl font-bold text-surface-100">
          $
          {portfolioValue.toLocaleString(undefined, {
            maximumFractionDigits: 0,
          })}
        </div>
      </div>

      {/* Target Positions */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-surface-300 mb-3">
          Target Positions ({sortedPositions.length})
        </h4>
        <div className="space-y-2">
          {sortedPositions.map(([ticker, weight]) => {
            const dollarAmount = weight * portfolioValue;
            const percentage = (weight * 100).toFixed(1);

            return (
              <div key={ticker} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-8 bg-surface-800 rounded flex items-center justify-center">
                    <span className="text-sm font-mono font-medium text-surface-200">
                      {ticker}
                    </span>
                  </div>
                  <div className="w-24 bg-surface-800 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full transition-all"
                      style={{ width: `${Math.min(weight * 100, 100)}%` }}
                    />
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-surface-200 font-medium">
                    $
                    {dollarAmount.toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })}
                  </span>
                  <span className="text-surface-500 text-sm ml-2">
                    {percentage}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Trades Section */}
      {trades.length > 0 ? (
        <div className="pt-4 border-t border-surface-700">
          <h4 className="text-sm font-medium text-surface-300 mb-3">
            Action Required ({trades.length} trades)
          </h4>

          {/* Sell Orders First */}
          {sellTrades.length > 0 && (
            <div className="mb-4">
              <div className="text-xs text-surface-500 uppercase tracking-wider mb-2">
                Sell Orders
              </div>
              <div className="space-y-2">
                {sellTrades.map((trade) => (
                  <TradeRow key={`sell-${trade.ticker}`} trade={trade} />
                ))}
              </div>
            </div>
          )}

          {/* Buy Orders */}
          {buyTrades.length > 0 && (
            <div>
              <div className="text-xs text-surface-500 uppercase tracking-wider mb-2">
                Buy Orders
              </div>
              <div className="space-y-2">
                {buyTrades.map((trade) => (
                  <TradeRow key={`buy-${trade.ticker}`} trade={trade} />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="pt-4 border-t border-surface-700">
          <div className="text-surface-400 text-sm text-center py-2">
            ✓ No rebalancing needed today
          </div>
        </div>
      )}
    </div>
  );
}

function TradeRow({ trade }: { trade: TradeOrder }) {
  const isSell = trade.action === "SELL";
  const weightChange = trade.targetWeight - trade.currentWeight;

  return (
    <div className="flex items-center justify-between p-2 rounded bg-surface-800/30">
      <div className="flex items-center gap-3">
        <span
          className={`text-xs font-semibold px-2 py-0.5 rounded ${
            isSell
              ? "bg-red-500/20 text-red-400"
              : "bg-green-500/20 text-green-400"
          }`}
        >
          {trade.action}
        </span>
        <span className="font-mono text-surface-200">{trade.ticker}</span>
      </div>
      <div className="flex items-center gap-4 text-sm">
        <div className="text-right">
          <div className="text-surface-300">
            {trade.shares.toFixed(2)} shares @ ${trade.price.toFixed(2)}
          </div>
          <div className="text-surface-500 text-xs">
            {(trade.currentWeight * 100).toFixed(1)}% →{" "}
            {(trade.targetWeight * 100).toFixed(1)}%
            <span className={isSell ? "text-red-400" : "text-green-400"}>
              {" "}
              ({weightChange >= 0 ? "+" : ""}
              {(weightChange * 100).toFixed(1)}%)
            </span>
          </div>
        </div>
        <div
          className={`font-medium ${isSell ? "text-red-400" : "text-green-400"}`}
        >
          $
          {trade.dollarAmount.toLocaleString(undefined, {
            maximumFractionDigits: 0,
          })}
        </div>
      </div>
    </div>
  );
}
